import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

export { Pagination, type PaginationProps } from "@/components/ui/pagination"

/*
   Four screens currently hand-roll a CSS grid with column widths repeated
   in the header and in every row — the queue ledger, the users list, the
   keys list and the client member list. They drift, and they broke at
   narrow widths in exactly the way that motivated this component:

     A rule like `> *:nth-child(n+3) { display: none }` counts DOM children,
     so the moment a decorative element joins the row — a timeline stem, a
     status dot — it eats the wrong column and leaves a bare button.

   The fix is that COLUMNS ARE OBJECTS, not positions. Each declares a
   `priority`, and visibility is computed from the container's measured
   width rather than from a media query against the viewport. That matters
   here specifically: the queue renders full-width, but the same table
   inside a drawer or a split pane is 380px wide while the viewport is
   1600px, and a media query gets that exactly backwards.

   Rows can expand — the version timeline under a queue thread is the
   reason. Expansion is a row-level render, so the expanded content is not
   forced into the column grid.
*/

export interface Column<T> {
  id: string
  header: React.ReactNode
  /** Grid track. "minmax(0,1fr)" for the column that should absorb slack. */
  width?: string
  align?: "start" | "end"
  /**
   * 1 always shows. 2 hides below ~860px of TABLE width, 3 below ~1080px.
   * Measured on the container, not the viewport — the same table in a
   * drawer is narrow while the window is wide.
   */
  priority?: 1 | 2 | 3
  sortable?: boolean
  /** Screen-reader header when `header` is decorative or empty. */
  srHeader?: string
  cell: (row: T) => React.ReactNode
}

export type SortState = { columnId: string; dir: "asc" | "desc" } | null

/**
 * Row identity type. String on purpose — passing numeric ids would silently
 * `String()`-coerce on Set membership tests and produce phantom re-selections.
 * Callers with numeric ids should format them explicitly at the boundary
 * (`String(row.id)`) so the coercion is visible.
 */
export type RowKey = string

export interface DataTableProps<T> {
  columns: Array<Column<T>>
  rows: T[]
  rowKey: (row: T) => RowKey
  /** Accessible name. Required — a table nobody can identify is a maze. */
  label: string
  sort?: SortState
  onSortChange?: (s: SortState) => void
  /** Makes rows activatable by click and by Enter. */
  onRowActivate?: (row: T) => void
  /** Rows the user has opened. Pair with `renderExpanded`. */
  expandedKeys?: Set<RowKey>
  onToggleExpand?: (key: RowKey) => void
  renderExpanded?: (row: T) => React.ReactNode

  /**
   * Selection. Opt-in, because a checkbox column that leads nowhere is a
   * column of dead weight — only pass these when a bulk action exists.
   * In this product that is two places: assigning several queued reviews
   * to one reviewer, and exporting a subset of dataset cases.
   */
  selectedKeys?: Set<RowKey>
  onSelectionChange?: (next: Set<RowKey>) => void
  /** Rendered above the table when anything is selected. */
  renderBulkActions?: (selected: Set<RowKey>) => React.ReactNode

  /** Header sticks while the body scrolls. For long, scrolled tables. */
  stickyHeader?: boolean
  /** Highlighted row — selection driven from elsewhere, e.g. a PDF. */
  activeKey?: RowKey | null
  loading?: boolean
  /** Number of skeleton rows to render while `loading`. Match this to
      the caller's `pageSize` so the table doesn't jump when data lands. */
  loadingRowCount?: number
  /** Rendered in place of rows when there are none. */
  empty?: React.ReactNode
  density?: "comfortable" | "compact"
  className?: string
}

const MIN_WIDTH: Record<2 | 3, number> = { 2: 860, 3: 1080 }

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  label,
  sort,
  onSortChange,
  onRowActivate,
  expandedKeys,
  onToggleExpand,
  renderExpanded,
  selectedKeys,
  onSelectionChange,
  renderBulkActions,
  stickyHeader = false,
  activeKey,
  loading = false,
  loadingRowCount = 8,
  empty,
  density = "comfortable",
  className,
}: DataTableProps<T>) {
  const ref = React.useRef<HTMLDivElement>(null)

  /* Store the BREAKPOINT, not the width.
     Writing the raw width on every pixel of a drag re-renders the table,
     which changes the grid template, which resizes the container, which
     notifies the observer again — and the browser reports the leftover
     notifications as "ResizeObserver loop completed with undelivered
     notifications". Nothing is broken when that fires, but it reaches
     Sentry and fails any test config that treats console errors as
     failures, so it is worth not causing.

     Two changes remove it: the value only changes three times across the
     whole range instead of hundreds, and the write is deferred to the next
     frame so the observer's own pass has finished before React lays out
     again. */
  const [tier, setTier] = React.useState<1 | 2 | 3>(3)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return

    let raf = 0
    let last: 1 | 2 | 3 | null = null

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return // spec-wise unreachable for a single observed element
      const w = entry.contentRect.width
      const next: 1 | 2 | 3 = w >= MIN_WIDTH[3] ? 3 : w >= MIN_WIDTH[2] ? 2 : 1
      if (next === last) return // the common case during a drag
      last = next
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        setTier(next)
      })
    })

    ro.observe(el)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [])

  const visible = React.useMemo(
    () => columns.filter((c) => !c.priority || c.priority <= tier),
    [columns, tier],
  )

  const expandable = Boolean(renderExpanded)
  const selectable = Boolean(selectedKeys && onSelectionChange)

  const template = [
    selectable ? "32px" : null,
    expandable ? "28px" : null,
    ...visible.map((c) => c.width ?? "minmax(0,1fr)"),
  ]
    .filter(Boolean)
    .join(" ")

  const allKeys = React.useMemo(() => rows.map(rowKey), [rows, rowKey])
  const selectedOnPage = allKeys.filter((k) => selectedKeys?.has(k)).length
  const allSelected = rows.length > 0 && selectedOnPage === rows.length
  /* Indeterminate is a property, not an attribute — React will not set it
     from JSX, so it has to go through a ref. */
  const headBox = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (headBox.current) {
      headBox.current.indeterminate = selectedOnPage > 0 && !allSelected
    }
  }, [selectedOnPage, allSelected])

  const toggleAll = () => {
    if (!selectedKeys || !onSelectionChange) return
    const next = new Set(selectedKeys)
    /* Only ever touches the current page. "Select all" that silently
       reaches across pagination is how someone exports 300 rows meaning
       to export 20. */
    allSelected
      ? allKeys.forEach((k) => next.delete(k))
      : allKeys.forEach((k) => next.add(k))
    onSelectionChange(next)
  }

  const toggleOne = (key: string) => {
    if (!selectedKeys || !onSelectionChange) return
    const next = new Set(selectedKeys)
    next.has(key) ? next.delete(key) : next.add(key)
    onSelectionChange(next)
  }

  const pad = density === "compact" ? "px-3 py-2" : "px-4 py-3"

  /* One tab stop for the whole body; arrows move between rows. A table of
     forty reviews should not cost forty tab presses to walk past. */
  const onKeyDown = (e: React.KeyboardEvent, index: number, row: T) => {
    const move = (to: number) => {
      e.preventDefault()
      const next = ref.current?.querySelector<HTMLElement>(
        `[data-row-index="${Math.min(Math.max(to, 0), rows.length - 1)}"]`,
      )
      next?.focus()
    }
    // Space selects (when selection is on), Enter activates (opens/expands).
    // Two different intentions — binding both to one key makes bulk selection
    // impossible by keyboard. When there is no selection column, Space falls
    // through to Enter's behaviour so the row still responds.
    if (e.key === "ArrowDown") move(index + 1)
    else if (e.key === "ArrowUp") move(index - 1)
    else if (e.key === "Home") move(0)
    else if (e.key === "End") move(rows.length - 1)
    else if (e.key === " " && selectable) {
      e.preventDefault()
      toggleOne(rowKey(row))
    } else if (e.key === "Enter" || (e.key === " " && !selectable)) {
      e.preventDefault()
      if (expandable) onToggleExpand?.(rowKey(row))
      else onRowActivate?.(row)
    }
  }

  return (
    <div
      ref={ref}
      role="table"
      aria-label={label}
      aria-rowcount={rows.length}
      aria-multiselectable={selectable || undefined}
      className={cn(
        "overflow-hidden rounded-lg border border-border bg-surface",
        className,
      )}
    >
      {selectedKeys && onSelectionChange && selectedKeys.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-b border-border bg-brand-tint px-4 py-2">
          <span className="font-id text-caption text-brand">{selectedKeys.size} selected</span>
          <button
            type="button"
            onClick={() => {
              onSelectionChange(new Set())
            }}
            className="text-caption text-text-2 underline underline-offset-2 hover:text-text"
          >
            Clear
          </button>
          <span className="flex-1" />
          {renderBulkActions?.(selectedKeys)}
        </div>
      )}

      <div
        role="row"
        className={cn(
          "grid items-center gap-3.5 border-b border-border bg-surface-sunken",
          density === "compact" ? "px-3 py-1.5" : "px-4 py-2",
          stickyHeader && "sticky top-0 z-sticky",
        )}
        style={{ gridTemplateColumns: template }}
      >
        {selectable && (
          <span role="columnheader">
            <input
              ref={headBox}
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              aria-label={
                allSelected
                  ? "Clear selection on this page"
                  : "Select every row on this page"
              }
              className="size-3.5 accent-[color:var(--brand)]"
            />
          </span>
        )}
        {expandable && <span role="columnheader" aria-label="Expand" />}
        {visible.map((c) => {
          const activeDir = sort && sort.columnId === c.id ? sort.dir : null
          const content = (
            <span className="font-id text-eyebrow uppercase tracking-[0.14em] text-text-3">
              {c.header}
            </span>
          )
          return (
            <div
              key={c.id}
              role="columnheader"
              aria-sort={
                activeDir === "asc"
                  ? "ascending"
                  : activeDir === "desc"
                    ? "descending"
                    : undefined
              }
              className={cn("min-w-0", c.align === "end" && "text-right")}
            >
              {c.sortable && onSortChange ? (
                <button
                  type="button"
                  onClick={() => {
                    onSortChange(
                      activeDir === "asc"
                        ? { columnId: c.id, dir: "desc" }
                        : { columnId: c.id, dir: "asc" },
                    )
                  }}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-xs",
                    "transition-colors duration-120 ease-out hover:text-text",
                  )}
                >
                  {content}
                  {activeDir === "asc" ? (
                    <ChevronUp className="size-3 text-text" aria-hidden="true" />
                  ) : activeDir === "desc" ? (
                    <ChevronDown className="size-3 text-text" aria-hidden="true" />
                  ) : (
                    <ChevronsUpDown
                      className="size-3 text-text-disabled"
                      aria-hidden="true"
                    />
                  )}
                </button>
              ) : (
                <>
                  {content}
                  {c.srHeader && <span className="sr-only">{c.srHeader}</span>}
                </>
              )}
            </div>
          )
        })}
      </div>

      {loading ? (
        <SkeletonRows
          columns={visible.length + (expandable ? 1 : 0) + (selectable ? 1 : 0)}
          template={template}
          pad={pad}
          rows={loadingRowCount}
        />
      ) : rows.length === 0 ? (
        <div className="px-4 py-12">{empty}</div>
      ) : (
        rows.map((row, i) => {
          const key = rowKey(row)
          const isOpen = expandedKeys?.has(key) ?? false
          const interactive = Boolean(onRowActivate || expandable)

          return (
            <React.Fragment key={key}>
              <div
                role="row"
                data-row-index={i}
                tabIndex={interactive ? (i === 0 ? 0 : -1) : undefined}
                aria-expanded={expandable ? isOpen : undefined}
                onClick={
                  interactive
                    ? (e) => {
                        // let buttons inside a row do their own thing
                        if ((e.target as HTMLElement).closest("button,a,input")) return
                        expandable ? onToggleExpand?.(key) : onRowActivate?.(row)
                      }
                    : undefined
                }
                onKeyDown={interactive ? (e) => onKeyDown(e, i, row) : undefined}
                className={cn(
                  "grid items-center gap-3.5 border-b border-border-soft outline-none",
                  pad,
                  interactive &&
                    "cursor-pointer transition-colors duration-120 ease-out hover:bg-brand-tint/60",
                  activeKey === key && "bg-brand-tint/70",
                  selectedKeys?.has(key) && "bg-brand-tint/40",
                  "focus-visible:bg-brand-tint/60 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-[color:var(--brand)]",
                )}
                style={{ gridTemplateColumns: template }}
              >
                {selectable && selectedKeys && (
                  <input
                    type="checkbox"
                    checked={selectedKeys.has(key)}
                    onChange={() => {
                      toggleOne(key)
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    aria-label={`Select row ${i + 1}`}
                    className="size-3.5 accent-[color:var(--brand)]"
                  />
                )}
                {expandable && (
                  <ChevronRight
                    aria-hidden="true"
                    className={cn(
                      "size-3.5 text-text-3 transition-transform duration-180 ease-out",
                      isOpen && "rotate-90",
                    )}
                  />
                )}
                {visible.map((c) => (
                  <div
                    key={c.id}
                    role="cell"
                    className={cn("min-w-0", c.align === "end" && "flex justify-end")}
                  >
                    {c.cell(row)}
                  </div>
                ))}
              </div>

              {/* Expanded content sits outside the column grid on purpose —
                  a version timeline is not four cells. */}
              {isOpen && renderExpanded && (
                <div role="row" className="border-b border-border-soft bg-bg">
                  <div role="cell">{renderExpanded(row)}</div>
                </div>
              )}
            </React.Fragment>
          )
        })
      )}
    </div>
  )
}

function SkeletonRows({
  columns,
  template,
  pad,
  rows,
}: {
  columns: number
  template: string
  pad: string
  rows: number
}) {
  /* Widths vary per cell so it reads as content rather than as a loading
     barcode, and the geometry matches a real row so nothing jumps.
     The shimmer sweeps via background-position (see skeletonBase in
     alert.tsx); we mirror that here to avoid pulling the primitive
     into the DataTable render path. */
  const widths = ["70%", "45%", "60%", "35%", "50%", "82%", "38%"]
  return (
    <div aria-busy="true" role="status" aria-label="Loading rows">
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className={cn("grid items-center gap-3.5 border-b border-border-soft", pad)}
          style={{ gridTemplateColumns: template }}
        >
          {Array.from({ length: columns }, (_, c) => (
            <div
              key={c}
              className={cn(
                "h-3 rounded-xs bg-surface-sunken",
                "bg-[linear-gradient(90deg,transparent_0%,rgb(255_255_255/0.55)_50%,transparent_100%)]",
                "dark:bg-[linear-gradient(90deg,transparent_0%,rgb(255_255_255/0.06)_50%,transparent_100%)]",
                "bg-no-repeat bg-[length:200%_100%] bg-[position:-150%_0]",
                "animate-shimmer motion-reduce:animate-none motion-reduce:bg-none",
              )}
              style={{
                width: widths[(r + c) % widths.length],
                animationDelay: `${(r + c) * 60}ms`,
              }}
            />
          ))}
        </div>
      ))}
      <span className="sr-only">Loading</span>
    </div>
  )
}

